import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AutoFitText from './AutoFitText';
import { formatCurrencyCompact } from '../utils/numberFormat';

const AnimatedNumber = ({ 
  value, 
  decimals = 2, 
  prefix = '', 
  suffix = '', 
  duration = 1.5,
  className = '',
  delay = 0,
  format = '',
  formatter,
  autoFit = false,
  compactDecimals = 1
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    // Reset to 0 when value changes
    setDisplayValue(0);
    
    const startTime = Date.now() + delay;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      
      // Smooth easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = value * easeOut;
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(value); // Ensure we end exactly at the target value
      }
    };
    
    if (delay > 0) {
      setTimeout(animate, delay);
    } else {
      animate();
    }
  }, [value, duration, delay]);

  const formatValue = (num) => {
    if (decimals === 0) {
      return Math.round(num).toLocaleString();
    }
    return num.toLocaleString(undefined, { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };
  
  const buildFormattedValue = (num) => {
    if (formatter) {
      return formatter(num);
    }
    if (format === 'currencyCompact') {
      const safePrefix = num < 0 && prefix.startsWith('+') ? prefix.slice(1) : prefix;
      return formatCurrencyCompact(num, {
        decimals,
        compactDecimals,
        prefix: safePrefix,
        suffix
      });
    }
    return `${prefix}${formatValue(num)}${suffix}`;
  };

  const formattedValue = buildFormattedValue(displayValue);
  const autoFitProps = typeof autoFit === 'object' ? autoFit : {};

  return (
    <motion.div 
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, delay: delay / 1000 }}
    >
      {autoFit ? (
        <AutoFitText
          minPx={autoFitProps.minPx ?? 14}
          maxPx={autoFitProps.maxPx ?? 48}
          textClassName={autoFitProps.textClassName ?? ''}
        >
          {formattedValue}
        </AutoFitText>
      ) : (
        <span>{formattedValue}</span>
      )}
    </motion.div>
  );
};

export default AnimatedNumber;
